import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldX, RefreshCw, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function AccessRevokedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [checking, setChecking] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const { data: userRole, isLoading: roleLoading } = useQuery({
    queryKey: ["user_role_church", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("church_id, role")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  if (!roleLoading && userRole?.church_id) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleCheckInvitation = async () => {
    if (!user?.email) return;
    setChecking(true);
    try {
      const { data, error } = await supabase
        .from("invitations")
        .select("token")
        .ilike("email", user.email)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      if (!data || data.length === 0) {
        toast({
          title: "No new invitation found",
          description: "Ask your church admin to send you a new invitation, then check again.",
        });
        return;
      }
      const { error: acceptError } = await supabase.rpc("accept_invitation" as any, {
        _token: data[0].token,
        _user_id: user.id,
      });
      if (acceptError) throw acceptError;
      queryClient.invalidateQueries({ queryKey: ["user_role_church"] });
      toast({ title: "Welcome back!", description: "You've rejoined the church." });
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Could not check invitations", variant: "destructive" });
    } finally {
      setChecking(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");
      if (error) {
        const status = (error as any).context?.status;
        if (status === 401) {
          await supabase.auth.signOut();
          window.location.href = "/login";
          return;
        }
        let message = error.message;
        try {
          const body = await (error as any).context?.json?.();
          if (body?.error) message = body.error;
        } catch { /* keep generic message */ }
        throw new Error(message);
      }
      if (data?.error) throw new Error(data.error);
      await supabase.auth.signOut();
      window.location.href = "/login";
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to delete account", variant: "destructive" });
      setDeleteLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <ShieldX className="w-7 h-7 text-destructive" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Your access has been revoked</h1>
          <p className="text-sm text-muted-foreground">
            A church admin has removed your access to their church. If you believe this was a
            mistake, contact your church admin and ask them to send you a new invitation.
          </p>
        </div>

        <div className="space-y-3">
          <Button onClick={handleCheckInvitation} disabled={checking} className="w-full gap-2">
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Check for a new invitation
          </Button>
          <Button variant="outline" onClick={() => navigate("/setup")} className="w-full">
            Create my own church
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="w-full gap-2 text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4" />
                Permanently delete my account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <span className="block">This will permanently delete your account and erase your profile data. This action <strong>cannot be undone</strong>.</span>
                  <span className="block mt-3">Type <strong>DELETE</strong> to confirm:</span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="mt-2"
              />
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={deleteConfirmText !== "DELETE" || deleteLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteAccount();
                  }}
                >
                  {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Delete Account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <button onClick={handleSignOut} className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4">
          Sign out
        </button>
      </div>
    </div>
  );
}
